import { useTranslation } from "react-i18next";
import styles from "./SubmitButton.module.css";

/**
 * Check icon component for success state
 */
const CheckIcon = () => (
	<svg className={styles.checkIcon} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

/**
 * Submit button component for submitting all predictions
 * @param {Object} props - Component props
 * @param {boolean} props.isButtonActive - Whether the button should be active
 * @param {boolean} props.isSubmitting - Whether submission is in progress
 * @param {boolean} props.hasSubmitted - Whether submission was successful
 * @param {boolean} props.hasChangedSinceSubmission - Whether predictions have been changed since last submission
 * @param {Function} props.onSubmit - Submit handler function
 * @param {number} props.matchesCount - Number of matches to submit
 * @returns {JSX.Element|null} - Submit button or null if no matches
 */
const SubmitButton = ({ isButtonActive, isSubmitting, hasSubmitted, hasChangedSinceSubmission, onSubmit, matchesCount }) => {
	const { t } = useTranslation();

	// Don't render if no matches
	if (matchesCount === 0) {
		return null;
	}

	// Determine button styling and behavior
	const isDisabled = !isButtonActive || isSubmitting;
	const shouldShowAsActive = isButtonActive && (isSubmitting || !hasSubmitted || hasChangedSinceSubmission);
	const shouldShowAsSaved = hasSubmitted && !hasChangedSinceSubmission && !isSubmitting;

	return (
		<div className={styles.submitButtonContainer}>
			<button
				className={`${styles.submitButton} ${shouldShowAsActive ? styles.submitButtonActive : ""} ${shouldShowAsSaved ? styles.submitButtonSaved : ""}`}
				onClick={onSubmit}
				disabled={isDisabled}
			>
				{isSubmitting ? (
					t("predictions.submitButton.submitting")
				) : hasSubmitted && !hasChangedSinceSubmission ? (
					<>
						<CheckIcon />
						{t("predictions.submitButton.predictionsSaved")}
					</>
				) : hasChangedSinceSubmission ? (
					t("predictions.submitButton.updatePredictions")
				) : (
					t("predictions.submitButton.submitAllPredictions")
				)}
			</button>
		</div>
	);
};

export default SubmitButton;
